import React, { forwardRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Input } from '@chakra-ui/react';
import { Base } from '../Base';
import buildInputTypes from './buildInputTypes';

export const TextField = forwardRef((props, ref) => {
  const {
    id,
    name,
    type,
    size,
    error,
    value,
    onBlur,
    onChange,
    autoFocus,
    maxLength,
    labelText,
    noOfLines,
    placeholder,
    autoComplete,
    displayAsPassword,
    'data-testid': dataTestId,
    ...contentProps
  } = props;

  const testId = dataTestId ? `${dataTestId}-input` : null;
  const inputType = useMemo(() => buildInputTypes({ type }), [type]);
  const isPasswordType = type === 'password' || displayAsPassword;
  const typePasswordDecorationProps = useMemo(() => (isPasswordType
    ? ({
      fontFamily: 'password',
      letterSpacing: 1.5,
      _placeholder: {
        letterSpacing: 'normal',
        fontFamily: 'proxima-nova',
      },
    })
    : {}
  ), [
    isPasswordType,
  ]);

  return (
    <Base
      {...contentProps}
      id={id}
      size={size}
      name={name}
      error={error}
      labelText={labelText}
      data-testid={dataTestId}
      noOfLines={noOfLines}
    >
      <Input
        size={size}
        ref={ref}
        name={name}
        value={value}
        onBlur={onBlur}
        onChange={onChange}
        isInvalid={!!error}
        data-testid={testId}
        maxLength={maxLength}
        type={inputType.type}
        borderColor="gray.400"
        as={inputType.inputAs}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        {...typePasswordDecorationProps}
      />
    </Base>
  );
});

TextField.defaultProps = {
  id: '',
  value: '',
  error: null,
  type: 'text',
  labelText: '',
  maxLength: null,
  noOfLines: null,
  placeholder: '',
  'data-testid': '',
  autoComplete: 'on',
  onBlur: () => {},
  onChange: () => {},
  displayAsPassword: false,
};

TextField.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  onBlur: PropTypes.func,
  error: PropTypes.shape({ message: PropTypes.string }),
  value: PropTypes.string,
  onChange: PropTypes.func,
  maxLength: PropTypes.number,
  labelText: PropTypes.string,
  noOfLines: PropTypes.number,
  placeholder: PropTypes.string,
  'data-testid': PropTypes.string,
  displayAsPassword: PropTypes.bool,
  name: PropTypes.string.isRequired,
  autoComplete: PropTypes.oneOf(['on', 'off', 'new-password', 'current-password']),
};
